import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	CzMaxPrice as ApiCzMaxPrice,
	ErrorCode,
	FinalCalculation,
	FinalCalculationProduct,
	Product,
	ProductUpdate,
	ValidationResult,
} from '~/Api/User/ProductGroup/CzMaxPrice';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/CzMaxPrice/ProductsTable/ProductsTable';
import ComplianceInfo from '~/CzSuklComplianceInfo';
import { parsePackageSize, PricedProduct, Product as GeneralProduct } from '~/Product';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import ActivityIndicator from '~/UI/ActivityIndicator';

import { ProductProvider } from './ProductProvider';
import { ReferencePriceValue } from './ProductsTable/Row/Row';
import styles from './styles.less';
import { Typography } from '~/libs/ui/atoms/Typography';
import { ProductGroupShowAllItemsSwitch } from '~/libs/ui/molecules/ProductGroupShowAllItemsSwitch';
import { ProductCalculation } from '~/pages/CzMaxPrice/src/components/ProductCalculation';
import { formatCountriesError } from '~/UI/formatCountriesError';
import {
	ProductParametersPanel,
	ReferenceValues,
	Status,
} from '~/pages/CzMaxPrice/src/components/ProductParametersPanel';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import { selectCzechExFactoryPrice } from './reducer';
import { Header } from '~/pages/CzMaxPrice/src/components/Header';
import ProductGroupNotificationSettingsModal from '~/ProductGroupNotificationSettingsModal';
import ProductGroupRenameModal from '~/ProductGroupRenameModal';
import ProductGroupDeleteModal from '~/ProductGroupDeleteModal';
import { SelectModal as ExchangeRateSelectModal } from '~/ExchangeRate';
import ProductGroupHistoryModal from '~/ProductGroupHistoryModal';
import { CreateModal as ProductGroupCreateModal } from '~/ProductGroup';

export enum ModalNames {
	history = 'history',
	notification = 'notification',
	rename = 'rename',
	delete = 'delete',
	create = 'create',
	copy = 'copy',
	exchangeRate = 'exchangeRate',
}

interface Props {
	header: ReactNode;
	group: UserProductGroup;
	czMaxPrice: ApiCzMaxPrice | null;
	referenceBasketVersion: ReferenceBasketVersion | null;
	pricedProducts: PricedProduct<Product>[];
	czechProducts: ProductRow[];
	suklReferencedProducts: ProductRow[];
	otherProducts: ProductRow[];
	alternativeProductsCount: number;
	validationResult: ValidationResult | null;
	versions: ReferenceBasketVersion[] | null;
	updateStatus: Status | null;
	isFetching: boolean;
	detailProductId: string | null;
	detailProductTabName: string;
	currency: string | null;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onCloseDetail(): void;
	onShowDetail(productId: string, tabName: string): void;
	onUpdate(group: UserProductGroup, packageSize: number | null, basketVersion: string, products: ProductUpdate[]): void;
	onSave(group: UserProductGroup): void;
	onCurrencyChange(currency: string): void;
	onQuery(a: string | null, b: string | null, c: string | null, d: boolean, e: boolean, f: boolean): void;
	onRemoved(group: UserProductGroup): void;
	onDeleteItems(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void): Promise<void>;
	onProductsExportCz(group: UserProductGroup, currency: string | null): Promise<void>;
	onEvidenceExport: (group: UserProductGroup) => Promise<void>;
	onCzMaxPriceExport: (group: UserProductGroup) => Promise<void>;
}

interface Values {
	reference: ReferenceValues;
	product: { [id: string]: ProductValues };
}

interface State {
	values: Values | null;
	lastSelectedProductId: string | null;
	lastSeenProductIdDetail: string | null;
	removeProduct: GeneralProduct | null;
	checkedRows: GeneralProduct[];
	modalName: string | null;
}

export default class CzMaxPrice extends Component<Props, State> {
	static defaultProps = {
		czMaxPrice: null,
		validationResult: null,
		detailProductId: null,
		detailProductTabName: 'info',
	};

	state: State = {
		values: null,
		lastSelectedProductId: null,
		lastSeenProductIdDetail: null,
		removeProduct: null,
		checkedRows: [],
		modalName: null,
	};

	componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	resolveValues(): Values {
		const {
			props: { czMaxPrice },
			state: { values },
		} = this;
		return this.createValues(czMaxPrice, values);
	}

	private readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	createValues = memoizeOne((czMaxPrice: ApiCzMaxPrice | null, values: Values | null): Values => {
		return {
			reference: values?.reference ?? {
				packageSize: {
					value: String(czMaxPrice?.reference.packageSize ?? ''),
					isValid: true,
				},
				basketVersion: czMaxPrice?.reference.referenceBasketVersion ?? null,
			},
			product:
				czMaxPrice?.products.reduce((products: { [id: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					const hasSelectedValidReferencePrice =
						currentValues !== undefined &&
						product.referencePrices.find(
							(referencePrice) => referencePrice.calculatedPrice.type === currentValues.referencePrice.value?.value
						) !== undefined;
					const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);

					products[product.product.id] = {
						referencePrice: hasSelectedValidReferencePrice
							? currentValues.referencePrice
							: {
									value:
										referencePrice !== undefined
											? {
													value: referencePrice.calculatedPrice.type,
													parentValue: null,
													prompt: null,
											  }
											: null,
									isValid: product.referencePrices.length === 0 || referencePrice !== undefined,
							  },
						packageSize: currentValues?.packageSize ?? {
							value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
							isValid: true,
						},
						packageSizeForSingleUnit: currentValues?.packageSizeForSingleUnit ?? {
							value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
							isValid: true,
						},
						selected: currentValues?.selected ?? product.selected,
					};
					return products;
				}, {}) ?? {},
		};
	});

	processValidationResult(validationResult: ValidationResult | null) {
		if (validationResult === null) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = {
			...values.reference,
			packageSize: {
				...values.reference.packageSize,
				isValid: !validationResult.referencePackageSizeIsMissing,
			},
		};
		const productValues = { ...values.product };

		validationResult.productErrors.forEach((error) => {
			if (error.productId === null || error.productId in productValues) {
				return;
			}
			const values = productValues[error.productId];

			if (error.code === ErrorCode.RequiredProductSettingsMissingErrorCode) {
				productValues[error.productId] = {
					...values,
					packageSize: { ...values.packageSize, isValid: false },
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.IllegalPriceTypeSelectedErrorCode) {
				productValues[error.productId] = {
					...values,
					referencePrice: { ...values.referencePrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	renderCompliance(productId: string): ReactNode {
		const { czMaxPrice } = this.props;
		const product = czMaxPrice?.products.find((product) => product.product.id === productId);
		return product !== undefined ? <ComplianceInfo compliance={product.productCompliance} /> : <Fragment />;
	}

	private readonly referenceValuesChanged = (reference: ReferenceValues) => {
		const values = this.resolveValues();
		const invokeSave = reference.basketVersion !== values.reference.basketVersion;

		this.setState({ values: { ...values, reference } }, () => {
			this.update();
			if (invokeSave) {
				this.props.onSave(this.props.group);
			}
		});
	};

	handleProductValuesChange = (
		item: Product,
		referencePrice: ReferencePriceValue | null,
		packageSize: string,
		packageSizeForSingleUnit: string
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const invokeSave = currentValue.referencePrice.value !== referencePrice;

		this.setState(
			{
				values: {
					reference: values.reference,
					product: {
						...values.product,
						[item.product.id]: {
							referencePrice: {
								value: referencePrice,
								isValid: item.referencePrices.length === 0 || referencePrice !== null,
							},
							packageSize: { value: packageSize, isValid: null },
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: null,
							},
							selected: currentValue.selected,
						},
					},
				},
			},
			() => {
				this.update();
				if (invokeSave) {
					this.props.onSave(this.props.group);
				}
			}
		);
	};

	productSelected = (item: Product, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				values: { reference: values.reference, product: productValues },
				lastSelectedProductId: item.product.id,
			},
			this.update
		);
	};

	selectProductRange(
		productValues: { [id: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	update = () => {
		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues = { ...values.product };

		const referencePackageSize = parsePackageSize(referenceValues.packageSize.value);
		referenceValues.packageSize.isValid = referencePackageSize.isValid;

		let hasValidationError = referencePackageSize.isValid === false;

		const products: ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { referencePrice, packageSize, packageSizeForSingleUnit } = values;

			const parsedPackageSize = parsePackageSize(packageSize.value);
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);

			hasValidationError =
				hasValidationError ||
				!referencePrice.isValid ||
				parsedPackageSize.isValid === false ||
				parsedPackageSizeForSingleUnit.isValid === false;

			productValues[productId] = {
				...values,
				packageSize: {
					...values.packageSize,
					isValid: parsedPackageSize.isValid,
				},
				packageSizeForSingleUnit: {
					...values.packageSizeForSingleUnit,
					isValid: parsedPackageSizeForSingleUnit.isValid,
				},
			};

			products.push(
				new ProductUpdate(
					productId,
					referencePrice.value?.value ?? null,
					parsedPackageSize.value,
					parsedPackageSizeForSingleUnit.value,
					values.selected
				)
			);
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError && referenceValues.basketVersion !== null) {
			this.props.onUpdate(this.props.group, referencePackageSize.value, referenceValues.basketVersion, products);
		}
	};

	handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	renderCalculationEmptyResult(finalCalculation: FinalCalculation): ReactNode {
		const countryError = finalCalculation.findOnlyOneProductPerCountryErrorCode();

		if (countryError !== null) {
			return (
				<strong>
					Calculation requires at most one product for each country
					{formatCountriesError(countryError.duplicateCountries)}
				</strong>
			);
		}
		if (finalCalculation.hasMissingProductSettings()) {
			return <strong>Some products settings are missing</strong>;
		}
		if (finalCalculation.isReferenceCountryProductMissing()) {
			return <strong>Calculation requires at least one reference country product</strong>;
		}
		if (finalCalculation.isMissingReferenceSettings()) {
			return <strong>Calculation requires reference settings</strong>;
		}
		if (finalCalculation.isMissingThreeReferenceCountryProducts()) {
			return <strong>Calculation requires three reference country products</strong>;
		}
		if (finalCalculation.isIllegalProductPriceTypeSelected()) {
			return <strong>Some products have illegal price selected</strong>;
		}
		return null;
	}

	createTopProductList = (
		topProducts: FinalCalculationProduct[],
		allProducts: Product[],
		productValues: { [id: string]: ProductValues },
		referencePackageSize: number | null
	) => {
		const updatedTopProductList = topProducts?.map((product) => {
			const productPackage = productValues?.[product.id];
			const productPrices = allProducts
				?.find((item) => item.product.id === product.id)
				?.referencePrices.find((item) => item.calculatedPrice.selected);
			const calculatedPrice = productPrices?.calculatedPrice ? productPrices?.calculatedPrice.price : null;

			const parsedPackageSize =
				productPackage.packageSize.value !== ''
					? parseHumanFloat(productPackage.packageSize.value)
					: referencePackageSize;

			return {
				id: product.id,
				dataSource: product.dataSource,
				calculatedPrice,
				parsedPackageSize,
				referencePackageSize,
			};
		});

		return updatedTopProductList;
	};

	private handleOnClose = (_: UserProductGroup): Promise<{ moduleError: string | null }> => {
		this.setState({ modalName: null });

		return Promise.resolve({ moduleError: null });
	};

	render() {
		const {
			props: {
				header,
				czMaxPrice,
				referenceBasketVersion,
				group,
				pricedProducts,
				czechProducts,
				suklReferencedProducts,
				otherProducts,
				alternativeProductsCount,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				onShowDetail,
				onSave,
				onQuery,
				onRemoved,
				onCurrencyChange,
				onProductsExportCz,
				onEvidenceExport,
				onCzMaxPriceExport,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, removeProduct, checkedRows, modalName },
		} = this;

		const values = this.resolveValues();
		const referencePackageSize = parsePackageSize(values.reference.packageSize.value).value;
		const { versions, updateStatus } = this.props;

		if (czMaxPrice === null && isFetching) {
			return <ActivityIndicator />;
		}

		const closeModal = () => {
			this.setState({ modalName: null });
		};

		const handleProductsExport = (currency: string) => {
			onProductsExportCz(group, currency);
		};

		return (
			<Fragment>
				{header}
				<br />
				<br />
				<Header
					group={group}
					currency={currency}
					name={group.name}
					lastUpdateTime={group.itemsUpdatedTime}
					parentName={group.parent?.name}
					parentId={group.parent?.id}
					exchangeRatesSettings={group.exchangeRates.settings}
					notificationSettingsLink={group.links.notificationSettings}
					notificationFrequency={group.notification.settings.frequency}
					onNotificationModal={() => this.setState({ modalName: ModalNames.notification })}
					onGroupHistoryModal={() => this.setState({ modalName: ModalNames.history })}
					onExchangeRateModal={() => this.setState({ modalName: ModalNames.exchangeRate })}
					onCopy={() => this.setState({ modalName: ModalNames.copy })}
					onRename={() => this.setState({ modalName: ModalNames.rename })}
					onDelete={() => this.setState({ modalName: ModalNames.delete })}
					handleModulePriceExport={() => onCzMaxPriceExport(group)}
					handleProductsEvidenceExport={() => onEvidenceExport(group)}
					handleProductsExport={handleProductsExport}
				/>
				<section className={styles.section}>
					{detailProductId !== null ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider czMaxPrice={czMaxPrice} id={detailProductId}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										status={this.renderCompliance(detailProductId)}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => onShowDetail(productId, detailProductTabName)}
										onSelectTab={(tabName) => onShowDetail(detailProductId, tabName)}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					{czMaxPrice !== null && referenceBasketVersion !== null && (
						<ProductCalculation
							topProducts={this.createTopProductList(
								czMaxPrice?.finalCalculation.fromProducts,
								czMaxPrice?.products,
								values.product,
								referencePackageSize
							)}
							threeLowestPricesAvg={czMaxPrice?.finalCalculation.threeLowestPricesAvg}
							secondAndThirdLowestPriceAvg={czMaxPrice?.finalCalculation.secondAndThirdLowestPriceAvg}
							deviation={czMaxPrice?.finalCalculation.deviation}
							exFactoryPrice={selectCzechExFactoryPrice(czMaxPrice)}
							emptyReason={this.renderCalculationEmptyResult(czMaxPrice?.finalCalculation)}
						/>
					)}

					<ProductParametersPanel
						values={values.reference}
						updateStatus={updateStatus}
						versions={versions}
						onChange={this.referenceValuesChanged}
						onSave={() => onSave(group)}
					/>

					{czMaxPrice?.products.length === 0 ? <ProductInstructions /> : null}

					{(czMaxPrice?.products?.length || 0) > 0 ? (
						<>
							<div className='relative max-w-[1260px] mx-auto pt-5 pb-2 px-[40px]'>
								<Typography level={2} weight='font-light' color='cBlue-800'>
									Czech product
								</Typography>
							</div>
							<ProductsTable
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								checkedRows={checkedRows}
								checkRow={this.checkRow}
								lastSelectedProductId={lastSelectedProductId}
								products={czechProducts}
								productValues={values.product}
								referencePackageSize={referencePackageSize}
								onChange={this.handleProductValuesChange}
								onSave={() => onSave(group)}
								onSelect={this.productSelected}
							/>

							<div className='max-w-[1260px] mx-auto pt-5 pb-2 px-[40px] flex flex-nowrap flex-row justify-between items-end'>
								<Typography level={2} weight='font-light' className='pr-4 whitespace-nowrap'>
									Reference basket products
								</Typography>
								<ProductGroupShowAllItemsSwitch alternativeProductsCount={alternativeProductsCount} />
							</div>
						</>
					) : null}

					{suklReferencedProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={suklReferencedProducts}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{otherProducts.length > 0 ? (
						<>
							<div className='relative max-w-[1260px] mx-auto pt-5 pb-2 px-[40px]'>
								<Typography level={2} weight='font-light'>
									Other
								</Typography>
							</div>
							{pricedProducts.length > 0 ? (
								<ProductsTable
									disableSelection
									detailLink={productDetailLink}
									highlightProductId={lastSeenProductIdDetail}
									checkedRows={checkedRows}
									checkRow={this.checkRow}
									lastSelectedProductId={lastSelectedProductId}
									products={otherProducts}
									productValues={values.product}
									referencePackageSize={referencePackageSize}
									onChange={this.handleProductValuesChange}
									onSave={() => onSave(group)}
									onSelect={this.productSelected}
								/>
							) : null}
						</>
					) : null}

					{removeProduct !== null ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => this.setState({ removeProduct: null })}
							onConfirm={async () => onSave(group)}
							onSuccess={() => this.setState({ removeProduct: null })}
						/>
					) : null}
					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}
				</section>

				{modalName === ModalNames.notification && (
					<ProductGroupNotificationSettingsModal
						group={group}
						onClose={closeModal}
						onChange={async () => {
							await onQuery(null, currency, 'ex-factory', false, false, false);
							closeModal();
						}}
					/>
				)}

				{modalName === ModalNames.copy && (
					<ProductGroupCreateModal
						groupType={group.type}
						templateGroup={group}
						onClose={closeModal}
						onSave={async (productGroup) => {
							await onSave(productGroup);
							closeModal();
						}}
					/>
				)}
				{modalName === ModalNames.rename && (
					<ProductGroupRenameModal group={group} onSave={this.handleOnClose} onClose={closeModal} />
				)}
				{modalName === ModalNames.delete && (
					<ProductGroupDeleteModal
						group={group}
						onClose={closeModal}
						onConfirm={async (productGroup) => {
							await onRemoved(productGroup);
							closeModal();
						}}
					/>
				)}

				{modalName === ModalNames.history && <ProductGroupHistoryModal group={group} onClose={closeModal} />}

				{modalName === ModalNames.exchangeRate && (
					<ExchangeRateSelectModal group={group} initiallySelectedDate={null} onClose={closeModal} />
				)}
			</Fragment>
		);
	}
}
